import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Typography,
  Tag,
  Button,
  Space,
  Spin,
  message,
  Alert,
  Tooltip,
  List,
} from 'antd';
import {
  LinkOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { apiService, WizardSession } from '../services/api';

const { Text, Title } = Typography;

interface WizardStatusProps {
  projectId: number;
  projectStatus?: string;
}

const wizardStatusConfig = {
  pending: { color: 'default', icon: <ClockCircleOutlined />, label: 'Pending' },
  in_progress: { color: 'processing', icon: <ClockCircleOutlined />, label: 'In Progress' },
  completed: { color: 'success', icon: <CheckCircleOutlined />, label: 'Completed' },
  expired: { color: 'error', icon: <ExclamationCircleOutlined />, label: 'Expired' },
};

export const WizardStatus: React.FC<WizardStatusProps> = ({ projectId, projectStatus }) => {
  const [wizardSessions, setWizardSessions] = useState<WizardSession[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchWizardSessions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getProjectWizardSessions(projectId);
      setWizardSessions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch wizard sessions:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchWizardSessions();
  }, [fetchWizardSessions]);

  const copyToClipboard = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      message.success('Wizard link copied to clipboard');
    } catch (err) {
      message.error('Failed to copy link');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getLatestActiveSession = () => {
    return wizardSessions.find(s => s.wizardStatus === 'pending' || s.wizardStatus === 'in_progress');
  };

  const getCompletedSession = () => {
    return wizardSessions.find(s => s.wizardStatus === 'completed');
  };

  if (loading) {
    return (
      <Card title="Client Onboarding Wizard">
        <div style={{ textAlign: 'center', padding: 20 }}>
          <Spin />
        </div>
      </Card>
    );
  }

  const latestActiveSession = getLatestActiveSession();
  const completedSession = getCompletedSession();

  return (
    <Card 
      title={
        <Space>
          <LinkOutlined />
          Client Onboarding Wizard
        </Space>
      }
      extra={
        <Button 
          icon={<ReloadOutlined />} 
          onClick={fetchWizardSessions}
          size="small"
        >
          Refresh
        </Button>
      }
    >
      {/* Status Summary */}
      {completedSession ? (
        <Alert
          type="success"
          message="Wizard Completed"
          description={
            <span>
              Completed on {formatDate(completedSession.completedAt!)}
              {completedSession.completedBy && (
                <span> by {completedSession.completedBy.username || completedSession.completedBy.email}</span>
              )}
            </span>
          }
          style={{ marginBottom: 16 }}
        />
      ) : latestActiveSession ? (
        <Alert
          type="info"
          message="Active Wizard Link"
          description={
            <div>
              <div style={{ marginBottom: 8 }}>
                Status: <Tag color={wizardStatusConfig[latestActiveSession.wizardStatus].color}>
                  {wizardStatusConfig[latestActiveSession.wizardStatus].label}
                </Tag>
                {latestActiveSession.currentStep > 0 && (
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    Step {latestActiveSession.currentStep + 1} of 4
                  </Text>
                )}
              </div>
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary">Expires: {formatDate(latestActiveSession.expiresAt)}</Text>
              </div>
              <Space>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(latestActiveSession.wizardLink!)}
                >
                  Copy Link
                </Button>
                <Button
                  size="small"
                  type="link"
                  icon={<LinkOutlined />}
                  href={latestActiveSession.wizardLink}
                  target="_blank"
                >
                  Open Wizard
                </Button>
              </Space>
            </div>
          }
          style={{ marginBottom: 16 }}
        />
      ) : projectStatus === 'preparing' ? (
        <Alert
          type="info"
          message="Onboarding Wizard Coming Soon"
          description="The onboarding wizard will be available shortly. You will receive an email with instructions."
          style={{ marginBottom: 16 }}
        />
      ) : (
        <Alert
          type="info"
          message="Wizard Available in Preparing Phase"
          description="The onboarding wizard will be available when the project moves to the 'Preparing' phase."
          style={{ marginBottom: 16 }}
        />
      )}

      {/* All Wizard Sessions */}
      {wizardSessions.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Title level={5}>Wizard History</Title>
          <List
            size="small"
            dataSource={wizardSessions}
            renderItem={(session) => (
              <List.Item
                actions={[
                  <Tooltip title="Copy wizard link" key="copy">
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(session.wizardLink!)}
                      disabled={session.wizardStatus === 'expired'}
                    />
                  </Tooltip>,
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Tag color={wizardStatusConfig[session.wizardStatus].color}>
                        {wizardStatusConfig[session.wizardStatus].icon}
                        {' '}
                        {wizardStatusConfig[session.wizardStatus].label}
                      </Tag>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Created: {formatDate(session.createdAt)}
                      </Text>
                    </Space>
                  }
                  description={
                    session.wizardStatus === 'completed' ? (
                      <Text type="secondary">
                        Completed on {formatDate(session.completedAt!)}
                        {session.completedBy && ` by ${session.completedBy.username || session.completedBy.email}`}
                      </Text>
                    ) : session.wizardStatus === 'expired' ? (
                      <Text type="danger">Expired on {formatDate(session.expiresAt)}</Text>
                    ) : (
                      <Text type="secondary">Expires: {formatDate(session.expiresAt)}</Text>
                    )
                  }
                />
              </List.Item>
            )}
          />
        </div>
      )}

    </Card>
  );
};
